import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackCartAdd } from '@/hooks/useAnalytics';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NewArrivalsCarousel() {
  const [newArrivals, setNewArrivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isArtist } = useAuth();
  const { addToCart } = useCart();

  // Autoplay plugin with pause on hover
  const autoplayPlugin = React.useRef(
    Autoplay({ 
      delay: 4000, 
      stopOnInteraction: false,
      stopOnMouseEnter: true 
    })
  );

  useEffect(() => {
    fetchNewArrivals();
  }, []);

  const fetchNewArrivals = async () => {
    try {
      // Fetch only main gallery artworks (non-NSFW), sorted by newest first
      const response = await axios.get(`${API}/artworks/new-arrivals`);
      setNewArrivals(response.data);
    } catch (error) {
      console.error('Failed to fetch new arrivals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (e, artwork) => {
    e.preventDefault();
    e.stopPropagation();
    if (addToCart(artwork)) {
      trackCartAdd(artwork.id, artwork.title);
      toast.success('Added to cart');
    } else {
      toast.error('Already in cart');
    }
  };

  if (loading) {
    return (
      <section className="py-16 md:py-24" data-testid="new-arrivals-loading">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="text-center text-muted-foreground">Loading new arrivals...</div>
        </div>
      </section>
    );
  }

  if (newArrivals.length === 0) {
    return null; // Don't render section if no new arrivals
  }

  return (
    <section className="py-16 md:py-24 bg-secondary/30" data-testid="new-arrivals-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between mb-12"
        >
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="text-xs uppercase tracking-[0.2em] text-primary font-medium">
                Just Added
              </span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="new-arrivals-title">
              New Arrivals
            </h2>
            <p className="text-base text-muted-foreground mt-3 max-w-lg">
              Discover the latest additions to the collection
            </p>
          </div>
          <Link to="/gallery" className="mt-6 md:mt-0">
            <Button
              variant="ghost"
              className="group text-sm uppercase tracking-widest hover:bg-transparent"
              data-testid="view-all-new-btn"
            >
              View All
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </motion.div>

        {/* Carousel */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            plugins={[autoplayPlugin.current]}
            className="w-full"
            data-testid="new-arrivals-carousel"
          >
            <CarouselContent className="-ml-4 md:-ml-6">
              {newArrivals.map((artwork, index) => (
                <CarouselItem 
                  key={artwork.id} 
                  className="pl-4 md:pl-6 basis-[85%] sm:basis-1/2 md:basis-1/3 lg:basis-1/4"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="group"
                    data-testid={`new-arrival-item-${artwork.id}`}
                  >
                    <Link to={`/artwork/${artwork.id}`}>
                      {/* Image Container */}
                      <div className="relative aspect-[3/4] overflow-hidden bg-muted rounded-sm">
                        <img
                          src={artwork.image_url}
                          alt={artwork.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          data-testid={`new-arrival-image-${artwork.id}`}
                        />
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                        
                        {/* New Badge */}
                        <div className="absolute top-3 left-3">
                          <span className="bg-primary text-primary-foreground text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full font-medium">
                            New
                          </span>
                        </div>

                        {/* Quick Add Button - Only for buyers */}
                        {!isArtist && artwork.is_available && (
                          <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <Button
                              size="sm"
                              className="w-full rounded-full uppercase tracking-widest text-xs"
                              onClick={(e) => handleAddToCart(e, artwork)}
                              data-testid={`quick-add-btn-${artwork.id}`}
                            >
                              Quick Add
                            </Button>
                          </div>
                        )}

                        {/* Sold Out Badge */}
                        {!artwork.is_available && (
                          <div className="absolute bottom-3 left-3">
                            <span className="bg-secondary text-secondary-foreground text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full">
                              Sold
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Artwork Info */}
                      <div className="mt-4">
                        <h3 className="font-serif text-lg font-light tracking-tight line-clamp-1" data-testid={`new-arrival-title-${artwork.id}`}>
                          {artwork.title}
                        </h3>
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">
                          {artwork.category}
                        </p>
                        <p className="text-base font-medium mt-2" data-testid={`new-arrival-price-${artwork.id}`}>
                          ${artwork.price.toFixed(2)}
                        </p>
                      </div>
                    </Link>
                  </motion.div>
                </CarouselItem>
              ))}
            </CarouselContent>
            
            {/* Navigation Arrows */}
            <CarouselPrevious 
              className="hidden md:flex -left-4 lg:-left-6 bg-background/80 backdrop-blur-sm border-0 shadow-lg hover:bg-background"
              data-testid="carousel-prev-btn"
            />
            <CarouselNext 
              className="hidden md:flex -right-4 lg:-right-6 bg-background/80 backdrop-blur-sm border-0 shadow-lg hover:bg-background"
              data-testid="carousel-next-btn"
            />
          </Carousel>

          {/* Mobile Scroll Hint */}
          <div className="flex justify-center mt-6 md:hidden">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">
              Swipe to explore
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
